import {
  faArrowUp,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { Icon } from "@intility/bifrost-react";
import Section from "@intility/bifrost-react/Section";
import Sticky from "@intility/bifrost-react/Sticky";
import { useState } from "react";
import "./TableOfContents.css";

export interface TocNode {
  /** Target heading id (the `#anchor`). */
  id: string;
  /** Display label. */
  label: string;
}

export interface TocSection {
  heading: TocNode;
  /** Subsections; empty for a leaf section. */
  children: TocNode[];
}

function TocEntryLink({ node }: { node: TocNode }) {
  return (
    <a className="bf-title-link" href={`#${node.id}`}>
      {node.label}
    </a>
  );
}

/**
 * The collapsible "On this page" list. Every entry is plain clickable text
 * (`bf-title-link`) that jumps to its section; a section with subsections gets
 * a separate caret button that toggles its nested list open (collapsed by
 * default so long documents stay compact). Navigation and expand/collapse are
 * independent actions.
 */
export function TocList({ sections }: { sections: TocSection[] }) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <ol className="bf-toc">
      {sections.map((s) => {
        if (s.children.length === 0) {
          return (
            <li key={s.heading.id}>
              <TocEntryLink node={s.heading} />
            </li>
          );
        }
        const isOpen = open.has(s.heading.id);
        return (
          <li key={s.heading.id}>
            <span className="toc-section-row">
              <TocEntryLink node={s.heading} />
              <button
                type="button"
                className="toc-toggle"
                aria-expanded={isOpen}
                aria-label={`${isOpen ? "Collapse" : "Expand"} ${s.heading.label}`}
                onClick={() => toggle(s.heading.id)}
              >
                <Icon
                  icon={isOpen ? faChevronDown : faChevronRight}
                  className="toc-caret"
                />
              </button>
            </span>
            {isOpen && (
              <ol className="bf-toc">
                {s.children.map((c) => (
                  <li key={c.id}>
                    <TocEntryLink node={c} />
                  </li>
                ))}
              </ol>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The desktop right-rail table of contents: an unstyled `Sticky` wrapping a
 * `Section` with the collapsible list and a "Go to top" link. Pair it with a
 * `to-large` accordion holding a bare {@link TocList} for the mobile layout.
 */
export function TableOfContents({
  sections,
  topId,
}: {
  sections: TocSection[];
  /** Element id that "Go to top" scrolls to. */
  topId: string;
}) {
  return (
    <Sticky
      unstyled
      className="from-large"
      style={{ width: 240, flex: "none" }}
    >
      <Section>
        <Section.Header>On this page</Section.Header>
        <Section.Content>
          <TocList sections={sections} />
        </Section.Content>
        <Section.Content borderTop>
          <a
            href={`#${topId}`}
            className="bf-title-link"
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            <Icon icon={faArrowUp} marginRight />
            Go to top
          </a>
        </Section.Content>
      </Section>
    </Sticky>
  );
}
